import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import type { PerformanceMonth } from '../../../lib/api';

type NumericKey = 'on_time' | 'otif' | 'required' | 'sla' | 'call_off' | 'lines' | 'suppliers' | 'care_cases' | 'ncrs';

export interface ChartSeries {
  key: NumericKey;
  label: string;
  color: string;
  /** Percent series draw as a line; counts draw as bars. */
  kind: 'line' | 'bar';
}

interface PerformanceChartProps {
  title: string;
  description: string;
  months: PerformanceMonth[];
  series: ChartSeries[];
  /** Percent charts get a 0–100 axis. */
  percent?: boolean;
  /** Draws a dashed target line at this value. */
  target?: number;
  /** Adds a three-month rolling average of the first series. */
  trend?: boolean;
  note?: string;
  testId: string;
  /** Click a month to open its drill-down. */
  onMonthClick?: (month: PerformanceMonth) => void;
}

interface TooltipRow { name?: string; value?: number | string; color?: string; dataKey?: string | number }
interface TooltipProps { active?: boolean; label?: string | number; payload?: TooltipRow[] }

type ChartRow = PerformanceMonth & { trend: number | null };

/** Rolling average of the last three months that have a value. */
function withTrend(months: PerformanceMonth[], key: NumericKey): ChartRow[] {
  return months.map((month, i) => {
    const window = months.slice(Math.max(0, i - 2), i + 1)
      .map((m) => m[key])
      .filter((v): v is number => typeof v === 'number');
    const current = month[key];
    const trend = current === null || window.length === 0
      ? null
      : Math.round((window.reduce((a, b) => a + b, 0) / window.length) * 10) / 10;
    return { ...month, trend };
  });
}

/**
 * One monthly supplier-performance chart: the measure, an optional dashed
 * target, and an optional three-month trend line. Months with no judged lines
 * leave a gap rather than drawing a zero.
 */
export default function PerformanceChart({
  title, description, months, series, percent = false, target, trend = false, note, testId, onMonthClick,
}: PerformanceChartProps) {
  const rows = withTrend(months, series[0].key);
  const hasData = rows.some((r) => series.some((s) => typeof r[s.key] === 'number' && (r[s.key] as number) > 0));

  const renderTooltip = ({ active, label, payload }: TooltipProps) => {
    if (!active || !payload?.length) return null;
    const month = rows.find((r) => r.label === String(label));
    return (
      <div className="rounded border bg-background p-2 text-xs shadow space-y-0.5">
        <div className="font-medium">{label}</div>
        {payload.map((p) => (
          <div key={String(p.dataKey)} style={{ color: p.color }}>
            {p.name}: {p.value === null || p.value === undefined ? 'no data' : `${Number(p.value).toLocaleString()}${percent ? '%' : ''}`}
          </div>
        ))}
        {month && percent && <div className="text-muted-foreground">{month.lines.toLocaleString()} delivery lines</div>}
      </div>
    );
  };

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}{onMonthClick ? ' Click a month for the detail.' : ''}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground" data-testid={`${testId}-empty`}>
            No data for this selection.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart
              data={rows}
              margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
              style={onMonthClick ? { cursor: 'pointer' } : undefined}
              onClick={(state: { activeLabel?: string | number } | null) => {
                if (!onMonthClick || !state || state.activeLabel === undefined) return;
                const month = rows.find((r) => r.label === String(state.activeLabel));
                if (month) onMonthClick(month);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                tick={{ fontSize: 11 }}
                width={42}
                domain={percent ? [0, 100] : [0, 'auto']}
                tickFormatter={(v: number) => (percent ? `${v}%` : v.toLocaleString())}
              />
              <Tooltip content={renderTooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {series.map((s) => (s.kind === 'bar' ? (
                <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[3, 3, 0, 0]} />
              ) : (
                <Line key={s.key} dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2}
                  dot={{ r: 2 }} connectNulls={false} type="monotone" />
              )))}
              {trend && (
                <Line dataKey="trend" name="3-month trend" stroke="#64748b" strokeDasharray="5 4"
                  strokeWidth={2} dot={false} connectNulls type="monotone" />
              )}
              {typeof target === 'number' && (
                <ReferenceLine y={target} stroke="#dc2626" strokeDasharray="6 4"
                  label={{ value: `Target ${target}%`, position: 'insideTopRight', fontSize: 11, fill: '#dc2626' }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  );
}
